// 🧊 모션 브리핑 «컨테이너 하나» — 장표 종류·글 양 → 도형 목표 모양, 앞 장면 모양 → 이번 모양 변신(순수 함수).
//   정본 지도 `docs/01-plan/features/motion-style-pack.md` §12-5 · 그리는 곳 `MotionBriefStage.tsx`.
//   ★좌표는 컴포지션 px(1920×1080 이면 설계 단위 그대로)★ — 장표 레이아웃(`layouts.tsx`)과 같은 토큰(T·LINE_HEIGHT·px)으로
//   안 내용 높이를 어림해 도형이 글자를 품게 한다. 어림은 넉넉한 쪽으로 기운다(글자는 절대 자르지 않는다 —
//   도형은 배경일 뿐이고 안 내용은 잘리지 않으므로, 어림이 틀려도 최악은 «글자가 도형 가장자리에 걸침»이다).
//   난수·시계·프레임 간 상태 없음 — 어느 프레임이든 바로 계산된다(결정성).
import { springStep } from '../../lib/motion-kit/spring';
import { T, LINE_HEIGHT } from './tokens';

export interface BriefRect { x: number; y: number; w: number; h: number; r: number }
/** 세이프존 core 인셋(px) — `useSafeArea` 의 safeT/B/L/R 와 같은 값. */
export interface BriefInsets { top: number; bottom: number; left: number; right: number }
export interface BriefSlide { type: string; content?: Record<string, unknown> | null }

/** 도형 안쪽 여백(1920 기준 px) — 안 내용 레이아웃은 이 여백 안쪽에 놓인다. */
export const BRIEF_PAD = Object.freeze({ x: 64, y: 40 });

/**
 * 장표별 목표 모양(화면 폭·높이 비율 · 모서리 1920 기준 px) — 정본 지도 §12-5 초안 표를 09-30 실측으로 키움.
 *   높이는 «최소»다 — 글이 많으면 어림 높이까지 늘고, core 높이를 넘으면 폭부터 넓힌다.
 *   모서리는 대비를 크게(큰 숫자 64 ↔ 단계 14) — «크기만 바뀌는 인상»(부모 관찰 ③)을 줄인다.
 */
export const BRIEF_SHAPES: Readonly<Record<string, { w: number; h: number; r: number }>> = Object.freeze({
  fullText: { w: 0.76, h: 0.44, r: 36 },
  kineticTypo: { w: 0.80, h: 0.46, r: 36 },
  cardGrid: { w: 0.88, h: 0.56, r: 18 },
  checkList: { w: 0.68, h: 0.56, r: 26 },
  statRow: { w: 0.88, h: 0.44, r: 28 },
  bigNumber: { w: 0.48, h: 0.56, r: 64 },
  splitFocus: { w: 0.86, h: 0.48, r: 30 },
  stepFlow: { w: 0.90, h: 0.40, r: 14 },
  comparePair: { w: 0.86, h: 0.54, r: 20 },
  mediaFocus: { w: 0.70, h: 0.70, r: 16 },
  posterStack: { w: 1, h: 1, r: 0 },
});

/* ── 큰 제목 분리(착수문 «큰 동사형 제목이 위에») ─────────────────── */

/** 제목을 도형 위 캔버스에 따로 띄우는 장표 — 제목 + 여러 항목인 목록형 5종. 제목 장면(fullText·kineticTypo)·큰 숫자·분할·사진은 제외. */
export const BRIEF_TITLED: ReadonlySet<string> = new Set(['cardGrid', 'checkList', 'statRow', 'stepFlow', 'comparePair']);
/** 도형 위 큰 제목 글자 크기(1920 기준 px) — 장표 안 제목(72)보다 크고 표제(108)보다 작다. */
export const BRIEF_TITLE_PX = 84;
/** 제목 띠와 도형 사이 간격(1920 기준 px). */
export const BRIEF_TITLE_GAP = 28;

export interface BriefTitleBand { text: string; top: number; height: number; left: number; width: number; fontPx: number }

/** 이 장표의 도형 위 큰 제목 띠(px) — 없으면 null. 폭 = 글자 자리 전체 · 높이 = 줄 수 × 1.2. */
export function briefTitleBand(slide: BriefSlide, W: number, safe: BriefInsets): BriefTitleBand | null {
  if (!BRIEF_TITLED.has(slide.type)) return null;
  const text = str(slide.content?.title).trim();
  if (!text) return null;
  const k = W / 1920;
  const width = W - safe.left - safe.right;
  const lines = briefLineCount(text, BRIEF_TITLE_PX, width / k);
  return { text, top: safe.top, height: Math.round(lines * BRIEF_TITLE_PX * LINE_HEIGHT.tight * k), left: safe.left, width, fontPx: Math.round(BRIEF_TITLE_PX * k) };
}

/** 제목을 뺀 장표(도형 크기 어림용) — 제목이 도형 밖으로 나가면 안 내용 높이에서 빠진다. */
export const briefWithoutTitle = (slide: BriefSlide): BriefSlide => ({ type: slide.type, content: { ...(slide.content ?? {}), title: undefined } });

/** 제목 띠를 반영한 글자 자리와 어림용 장표 — 무대·나가는 무대·앞 도형 계산이 같은 식을 쓴다. */
export function briefLayoutInputs(slide: BriefSlide, W: number, avail: BriefInsets): { band: BriefTitleBand | null; avail: BriefInsets; fitSlide: BriefSlide } {
  const band = briefTitleBand(slide, W, avail);
  if (!band) return { band, avail, fitSlide: slide };
  const k = W / 1920;
  return { band, avail: { ...avail, top: band.top + band.height + Math.round(BRIEF_TITLE_GAP * k) }, fitSlide: briefWithoutTitle(slide) };
}

/* ── 도형 색(변신의 두 번째 축) ────────────────────────────────── */

/** 도형 색 종류 — 목록·비교·단계 = 종이(흰) · 큰 숫자 = 브랜드색 · 제목 장면 = 먹. 원쉐이프 `shapeIsBrand` 의 장표판. */
export type BriefFill = 'paper' | 'brand' | 'ink';
export const BRIEF_PAPER = '#FFFFFF';
export const BRIEF_INK = '#111827';
const FILL_OF: Readonly<Record<string, BriefFill>> = Object.freeze({ fullText: 'ink', kineticTypo: 'ink', bigNumber: 'brand' });
export const briefFillOf = (type: string): BriefFill => FILL_OF[type] ?? 'paper';
export const briefFillColor = (fill: BriefFill, brand: string): string => (fill === 'brand' ? brand : fill === 'ink' ? BRIEF_INK : BRIEF_PAPER);

const HEX6 = /^#[0-9a-fA-F]{6}$/;
const rgbOf = (hex: string): [number, number, number] => {
  const h = HEX6.test(hex) ? hex.slice(1) : 'FFFFFF';
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
};
const lum = (hex: string): number => {
  const ch = (v: number) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const [r, g, b] = rgbOf(hex);
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
};
/** WCAG 대비(1~21). */
export const briefContrast = (a: string, b: string): number => {
  const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m);
  return (x + 0.05) / (y + 0.05);
};
/** 이 배경 위 글자색 — 흰/먹 중 대비가 큰 쪽(밝은 브랜드색 = 먹 글자 · 어두운 = 흰 글자). */
export const briefTextOn = (bg: string): string => (briefContrast(bg, '#FFFFFF') >= briefContrast(bg, BRIEF_INK) ? '#FFFFFF' : BRIEF_INK);
/** 두 색을 p(0~1)로 섞는다(sRGB 선형 보간 · 결정론). */
export function briefMixHex(a: string, b: string, p: number): string {
  const q = p < 0 ? 0 : p > 1 ? 1 : p;
  const [ar, ag, ab] = rgbOf(a), [br, bg, bb] = rgbOf(b);
  const c = (x: number, y: number) => Math.round(x + (y - x) * q).toString(16).padStart(2, '0');
  return `#${c(ar, br)}${c(ag, bg)}${c(ab, bb)}`;
}

/** 도형 색에 맞춘 장표 글자색 — 종이는 그대로, 브랜드·먹 위는 대비가 큰 글자(강조색도 읽히는 쪽으로). */
export function briefContentColors<C extends { textMain?: string; textSub?: string; accent: string; isDark?: boolean }>(fill: BriefFill, base: C, brand: string): C {
  if (fill === 'paper') return base;
  const bg = briefFillColor(fill, brand);
  const text = briefTextOn(bg);
  const light = text === '#FFFFFF';
  const accent = fill === 'brand' ? text : briefContrast(base.accent, bg) >= 3 ? base.accent : text;
  return { ...base, textMain: text, textSub: light ? 'rgba(255,255,255,0.82)' : 'rgba(17,24,39,0.78)', accent, isDark: light };
}

/** 첫 장면(앞 장면 도형이 없을 때)의 씨앗 — 화면 가운데 작은 둥근 네모에서 자라난다. */
export const briefSeedRect = (W: number, H: number): BriefRect => {
  const k = W / 1920;
  const side = Math.round(140 * k);
  return { x: Math.round((W - side) / 2), y: Math.round((H - side) / 2), w: side, h: side, r: Math.round(36 * k) };
};

/* ── 글 양 어림 ─────────────────────────────────────────────── */

const glyphEm = (ch: string): number => {
  const c = ch.codePointAt(0) ?? 0;
  if ((c >= 0xac00 && c <= 0xd7a3) || (c >= 0x3130 && c <= 0x318f) || (c >= 0x4e00 && c <= 0x9fff) || (c >= 0xff00 && c <= 0xffef)) return 1.0;
  if (ch === ' ' || ch === '\u00A0') return 0.32;
  if (ch === '\u2060') return 0;
  if (/[A-Z0-9%]/.test(ch)) return 0.62;
  if (/[a-z]/.test(ch)) return 0.55;
  return 0.55;
};
const emOf = (s: string): number => { let w = 0; for (const ch of s) w += glyphEm(ch); return w; };

/** 어절 경계 그리디 줄 수 — 한 어절이 폭을 넘으면 글자 단위로 넘친다(`word-break: keep-all` 가정에서 넉넉하게). */
export function briefLineCount(text: string | undefined | null, fontPx: number, widthPx: number): number {
  const t = String(text ?? '').trim();
  if (!t || widthPx <= 0) return t ? 1 : 0;
  const cap = widthPx / fontPx; // 한 줄 em 용량
  let lines = 0;
  for (const para of t.split('\n')) {
    let cur = 0;
    let n = 1;
    // ★일반 띄어쓰기에서만 줄이 갈린다 — «세\u00A0가지»·«→\u00A0다음» 같은 붙임(briefGlueText)은 한 덩이로 센다.
    for (const word of para.split(/[ \t]+/).filter(Boolean)) {
      const w = emOf(word);
      const add = cur === 0 ? w : cur + 0.32 + w;
      if (add <= cap) { cur = add; continue; }
      if (cur > 0) n += 1;
      cur = w;
      while (cur > cap) { n += 1; cur -= cap; }
    }
    lines += n;
  }
  return Math.max(1, lines);
}

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const textBlock = (text: string, font: number, lh: number, width: number): number =>
  text.trim() ? briefLineCount(text, font, width) * font * lh : 0;

/* ── 무대 글자 전처리 — 09-30 회장님 «글자가 폭을 어기면 안 된다» ──
 *   ① 어절 붙임: 숫자와 뒤 한글(단위·조사) 사이(U+2060 · «4 / 억은» 방지) · «→» 와 다음 어절 사이(U+00A0 · «→» 가 줄 끝에 홀로 남지 않게).
 *   ② 가운데 정렬: 무대 안 장표는 저장된 anchor 와 무관하게 가운데 — 도형이 글자 블록을 감싸므로 오른쪽·아래 정렬은 들쭉날쭉만 만든다
 *      (주제2 33초 «→ 증여로 판단, 증여세 / 부과 가능»). 포스터(전체 화면)는 제외. 저장 데이터는 그대로다(렌더 시점 변환). */
const NON_TEXT_KEYS: ReadonlySet<string> = new Set(['src', 'cardStyle', 'anchor', 'revealMode', 'keywordEmphasis', 'cols', 'value', 'type']);
/** 수사(한·두·세… / 숫자) + 단위(가지·개·번·명·년·원…) 사이 띄어쓰기 — 줄이 갈리면 «세 / 가지»(10-01 회장님 «단어 잘리는 것만 없게»). */
const NUM_UNIT = /(^|[\s«"'(\[·,/])((?:한|두|세|네|다섯|여섯|일곱|여덟|아홉|열|스무|몇|여러|\d+(?:[.,]\d+)?))[ \t]+(?=(?:가지|개월|개|번|명|년|달|주|시간|분|초|원|억|만|천|곳|살|배|차례|단계|장|권|건|회|%|퍼센트|줄|쪽))/g;
/** 꾸미는 말 + 의존명사(때·수·것·줄·적·뿐·듯·만큼) 사이 띄어쓰기 — «살 / 때»·«할 / 수» 도 같은 부류의 잘림(10-01 · 주제2 첫 장면 «재산을 살 / 때,»). */
const DEP_NOUN = /([가-힣])[ \t]+(?=(?:때|수|것|줄|적|뿐|듯|만큼)(?:이|가|은|는|을|를|에|에는|에도|에서|도|만|의|로|이다|입니다|이에요|예요|처럼|보다|까지|부터|마다)?(?:[\s,.!?…·)」』"']|$))/g;
export const briefGlueText = (t: string): string => t
  .replace(/(\d)(?=[가-힣])/g, '$1\u2060')
  .replace(NUM_UNIT, '$1$2\u00A0')
  .replace(DEP_NOUN, '$1\u00A0')
  .replace(/→ +/g, '→\u00A0');
function glueDeep(v: unknown, key?: string): unknown {
  if (typeof v === 'string') return key && NON_TEXT_KEYS.has(key) ? v : briefGlueText(v);
  if (Array.isArray(v)) return v.map((x) => glueDeep(x, key));
  if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([k, x]) => [k, glueDeep(x, k)]));
  return v;
}

/** 🧊 고아 방지 — 어절이 3개 이상이면 마지막 두 어절을 붙인다(한 줄에 넉넉히 들어갈 때만 · 폭 모델 × 여유).
 *  `text-wrap: balance` 는 가장 긴 줄을 줄이는 규칙이라 «서명 전에 꼼꼼히 / 확인하세요»처럼 마지막 줄 한 어절을 고르기도 한다(10-01 전 프레임 검사). */
export function briefGlueLastWords(t: string, fontPx: number, widthPx: number): string {
  const words = String(t ?? '').trim().split(/[ \t]+/).filter(Boolean);
  if (words.length < 3 || fontPx <= 0 || widthPx <= 0) return t;
  const pair = words.slice(-2).join('\u00A0');
  if (emOf(pair) * fontPx * BRIEF_WIDTH_SLACK > widthPx) return t;
  const i = t.replace(/[ \t]+$/, '').search(/[ \t]+\S+$/);
  return i < 0 ? t : t.slice(0, i) + t.slice(i).replace(/^[ \t]+/, '\u00A0');
}

/** 모션 타이포 도형 높이 어림의 굵은 글씨 여유(extrabold 폭을 8% 넉넉히 · 어림은 넉넉한 쪽이 안전). */
export const BRIEF_BOLD_SLACK = 1.08;
/** 무대에 내려보낼 장표 내용 — 어절 붙임 + 가운데 정렬(포스터 제외). */
export function briefStageContent(type: string, content: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const glued = glueDeep(content ?? {}) as Record<string, unknown>;
  return type === 'posterStack' ? glued : { ...glued, anchor: 'center' };
}

/* ── 칸 계획(단계·카드) — 09-30 회장님 «글자가 폭을 어기거나 바운더리를 벗어나는 건 절대 생기면 안 된다» ──
 *   칸은 내용에 맞춘다(내용을 칸에 우겨넣지 않는다): 가장 긴 라벨이 **한 줄**로 들어가는 칸 폭을 먼저 잡고,
 *   한 줄에 다 안 들어가면 ① 라벨 글자를 42px 하한까지만 줄이고 ② 그래도 안 되면 칸을 여러 줄로 쌓는다(글자를 접지 않는다).
 *   도형 폭은 이 줄 폭에 맞춰 넓어진다(briefFit). 레이아웃은 무대 컨텍스트 `cells` 로 같은 계획을 받는다. */
/** 한 줄 라벨의 글자 하한(1920 기준 px). */
export const BRIEF_LABEL_MIN_PX = 42;
/** 글자 폭 모델 여유 — 브라우저 실측(4벌 라벨 전부)의 최대 오차보다 크게 둔다(PR 본문 «폭 모델 오차» 표). */
export const BRIEF_WIDTH_SLACK = 1.1;
/** 한 줄 글자 폭 어림(1920 기준 px) — 굵은 글자는 4% 더. */
export const briefTextWidth = (text: unknown, px: number, bold = false): number => emOf(str(text).trim()) * px * (bold ? 1.04 : 1) * BRIEF_WIDTH_SLACK;
/** 칸 좌우 크롬(카드 안쪽 여백 28×2 + 테두리). */
const CELL_CHROME = 58;
/** 단계 사이 화살표 자리(화살표 + 좌우 간격 20×2). */
const STEP_JOINT = T.body * 0.9 + 2 * 20;
const CARD_GAP = 32;
export interface BriefCellPlan { labelPx: number; perRow: number }

/** 이 계획으로 한 줄(칸 perRow 개)에 필요한 폭(1920 기준 px) — 칸 계획이 없는 종류는 null. */
export function briefCellRowWidth(slide: BriefSlide, plan: BriefCellPlan): number | null {
  const c = slide.content ?? {};
  if (slide.type === 'stepFlow') {
    const need = Math.max(56, ...arr(c.steps).map((st) => briefTextWidth(st, plan.labelPx))) + CELL_CHROME;
    return plan.perRow * need + (plan.perRow - 1) * STEP_JOINT;
  }
  if (slide.type === 'cardGrid') {
    const cards = arr(c.cards) as Array<Record<string, unknown>>;
    const need = Math.max(0, ...cards.map((cd) => Math.max(briefTextWidth(cd.title, plan.labelPx, true), str(cd.badge) ? briefTextWidth(cd.badge, T.label, true) : 0))) + CELL_CHROME;
    return plan.perRow * need + (plan.perRow - 1) * CARD_GAP;
  }
  return null;
}

/** 안쪽 폭 `innerW`(1920 기준 px)에서 라벨이 한 줄로 들어가는 칸 계획 — 한 줄에 칸을 가장 많이, 글자를 가장 크게. 칸 계획이 없는 종류는 null. */
export function briefCellPlan(slide: BriefSlide, innerW: number): BriefCellPlan | null {
  const c = slide.content ?? {};
  const n = slide.type === 'stepFlow' ? arr(c.steps).length : slide.type === 'cardGrid' ? arr(c.cards).length : 0;
  if (!n) return null;
  const base = slide.type === 'stepFlow' ? T.caption : T.body;
  const maxPer = slide.type === 'stepFlow' ? n : Math.min(c.cols === 3 ? 3 : 2, n);
  const sizes = base > BRIEF_LABEL_MIN_PX ? [base, BRIEF_LABEL_MIN_PX] : [base];
  for (let per = maxPer; per >= 1; per--) {
    for (const px of sizes) {
      const plan = { labelPx: px, perRow: per };
      if ((briefCellRowWidth(slide, plan) ?? Infinity) <= innerW) return plan;
    }
  }
  return { labelPx: sizes[sizes.length - 1], perRow: 1 };
}

/**
 * 안 내용 높이 어림(1920 기준 px · 폭 `cw` 는 도형 안쪽 폭). `layouts.tsx` 의 토큰·여백을 그대로 따른다.
 *   반환 null = 어림하지 않는 종류(모양 표 높이 그대로).
 */
/** 촘촘한 체크리스트(무대 안 · 보통 간격으로 안 들어갈 때만) — 항목 간격 10px · 줄 높이 1.3배(글자 높이의 1.3배가 하한 · 09-30 부모 판단). */
export const BRIEF_COMPACT = Object.freeze({ gap: 10, lineHeight: 1.3 });
/** 촘촘한 단계·카드(무대 안 · 보통 간격으로 안 들어갈 때만) — 글자는 그대로 두고 칸 안 위아래 여백·간격·줄 높이부터 줄인다(가로 여백 28 은 그대로 = 칸 폭 계획 불변).
 *  단계: 칸 위아래 14 · 번호 원 48 · 번호↔글 8 · 줄 간격 12 · 글 줄 높이 1.3 / 카드: 칸 위아래 16 · 항목 간격 8 · 최소 높이 없음 · 본문 줄 높이 1.35. */
/** 📐 #6354 한 문장 장표 부제 두 열 — 줄바꿈 목록이 이 줄 수 이상일 때만 · 열 사이(1920 기준 px · 장표 층 «한 줄로»와 같은 96). */
export const BRIEF_SUB_COLS_MIN_LINES = 4;
export const BRIEF_SUB_COL_GAP = 96;
/** 부제(줄바꿈 목록)를 앞·뒤 반으로 나눈 두 열 — 줄이 모자라면 null(한 덩이 그대로). 그리기(layouts·컬러 브리핑)와 어림이 같은 함수를 쓴다. */
export function briefFullTextSubCols(sub: unknown): [string, string] | null {
  const lines = str(sub).split('\n').filter((l) => l.trim());
  if (lines.length < BRIEF_SUB_COLS_MIN_LINES) return null;
  const half = Math.ceil(lines.length / 2);
  return [lines.slice(0, half).join('\n'), lines.slice(half).join('\n')];
}
/** 📐 #6354 한 문장 장표가 촘촘히·두 열로도 안 들어갈 때의 글자 줄임 하한 — 장표 층 자리 맞추기(`SLIDE_CAPTION_FIT_MIN_SCALE`)와 같은 0.5.
 *  보통 하한(0.72)에서 멈추면 남는 글이 위로는 화면 밖 · 아래로는 자막 띠 밑으로 넘쳤다(9줄 목록 재현). */
export const BRIEF_FULLTEXT_MIN_SCALE = 0.5;

export const BRIEF_COMPACT_CELL = Object.freeze({ stepPadY: 14, stepCircle: 48, stepGap: 8, stepRowGap: 12, stepLine: 1.3, cardPadY: 16, cardGap: 8, cardBodyLine: 1.35 });

export function briefContentHeight(slide: BriefSlide, cw: number, compact = false, subCols = false): number | null {
  const c = slide.content ?? {};
  const title = str(c.title);
  const header = title ? textBlock(title, T.header, LINE_HEIGHT.tight, cw) : 0;
  switch (slide.type) {
    case 'fullText': {
      const sub = str(c.sub);
      // 📐 #6354 무대가 «촘촘히»(부제 줄 높이 1.3)·«두 열»(긴 목록을 반씩 · 열 사이 96)을 요구하면 같은 값으로 잰다. 기본은 종전 식 그대로.
      const lh = compact ? BRIEF_COMPACT.lineHeight : LINE_HEIGHT.normal;
      const cols = subCols ? briefFullTextSubCols(sub) : null;
      const subH = cols
        ? Math.max(...cols.map((col) => textBlock(col, T.body, lh, (cw - BRIEF_SUB_COL_GAP) / 2)))
        : textBlock(sub, T.body, lh, cw);
      return textBlock(title, T.hero, LINE_HEIGHT.tight, cw) + (sub ? 28 + subH : 0);
    }
    case 'kineticTypo': {
      // 모션 타이포는 문장이 2개 이상이면 **내레이션 문장을 번갈아** 크게 띄우고 제목은 머리말로 물러난다(kinetic-typo.tsx).
      //   그래서 가장 긴 문장으로 잰다 — 제목만 재면 긴 문장이 도형을 넘쳤다(09-30 dev 실측 «…차근차근 짚어 드리겠습니다»).
      //   글자는 extrabold 라 폭을 8% 넉넉히 본다.
      const beats = arr(c.beatTexts).map(str).filter((b) => b.trim());
      const heroW = cw / BRIEF_BOLD_SLACK;
      if (beats.length >= 2) {
        const hero = Math.max(...beats.map((b) => textBlock(b, T.hero, LINE_HEIGHT.tight, heroW)));
        return (title ? T.body * LINE_HEIGHT.normal + 20 : 0) + hero;
      }
      const sub = str(c.sub);
      return textBlock(title, T.hero, LINE_HEIGHT.tight, heroW) + (sub ? 32 + textBlock(sub, T.body, LINE_HEIGHT.normal, cw) : 0);
    }
    case 'checkList': {
      const items = arr(c.items).map(str);
      const tw = Math.min(cw, 1280) - 52 - 24;
      const lh = compact ? BRIEF_COMPACT.lineHeight : LINE_HEIGHT.normal;
      const rows = items.reduce((a, it) => a + Math.max(52, textBlock(it, T.body, lh, tw)), 0);
      return (header ? header + 44 : 0) + rows + Math.max(0, items.length - 1) * (compact ? BRIEF_COMPACT.gap : 24);
    }
    case 'cardGrid': {
      const cards = arr(c.cards) as Array<Record<string, unknown>>;
      const plan = briefCellPlan(slide, cw);
      const perRow = plan ? plan.perRow : c.cols === 3 ? 3 : 2;
      const titlePx = plan ? plan.labelPx : T.body;
      const cardW = (cw - 32 * (perRow - 1)) / perRow - 56;
      const K = BRIEF_COMPACT_CELL;
      const gap = compact ? K.cardGap : 14;
      const cardH = cards.map((cd) => Math.max(compact ? 0 : 180,
        (compact ? 2 * K.cardPadY : 48) + (str(cd.badge) ? T.label * LINE_HEIGHT.tight + gap : 0)
        + textBlock(str(cd.title), titlePx, LINE_HEIGHT.tight, cardW)
        + arr(cd.body).map(str).reduce((a, l) => a + gap + textBlock(l, T.caption, compact ? K.cardBodyLine : LINE_HEIGHT.loose, cardW), 0)));
      let rows = 0;
      for (let i = 0; i < cardH.length; i += perRow) rows += Math.max(...cardH.slice(i, i + perRow)) + (i ? 32 : 0);
      return (header ? header + 48 : 0) + rows;
    }
    case 'statRow': {
      const stats = arr(c.stats) as Array<Record<string, unknown>>;
      const n = Math.max(1, stats.length);
      const colW = (cw - 48 * (n - 1)) / n;
      const col = Math.max(0, ...stats.map((st) =>
        textBlock(str(st.value), T.hero, LINE_HEIGHT.tight, colW) + 16 + textBlock(str(st.label), T.caption, LINE_HEIGHT.normal, colW)));
      return (header ? header + 56 : 0) + col;
    }
    case 'bigNumber': {
      const label = str(c.label), sub = str(c.sub);
      return (label ? textBlock(label, T.body, LINE_HEIGHT.normal, cw) + 20 : 0)
        + textBlock(str(c.value), T.display, LINE_HEIGHT.tight, cw)
        + (sub ? 24 + textBlock(sub, T.body, LINE_HEIGHT.normal, cw) : 0);
    }
    case 'splitFocus': {
      const w = Math.min(cw, 1600);
      const leftW = w * 0.36, rightW = w - leftW - 56 * 2 - 1;
      const left = textBlock(str(c.label), T.title, LINE_HEIGHT.tight, leftW);
      const sub = str(c.sub);
      const right = textBlock(str(c.body), T.body, LINE_HEIGHT.normal, rightW) + (sub ? 20 + textBlock(sub, T.caption, LINE_HEIGHT.loose, rightW) : 0);
      return Math.max(left, right);
    }
    case 'stepFlow': {
      const steps = arr(c.steps).map(str);
      const plan = briefCellPlan(slide, cw);
      const per = Math.max(1, plan ? plan.perRow : steps.length);
      const px = plan ? plan.labelPx : T.caption;
      const rows = Math.ceil(Math.max(1, steps.length) / per);
      const stepW = (cw - (per - 1) * STEP_JOINT) / per - CELL_CHROME;
      const K = BRIEF_COMPACT_CELL;
      const col = Math.max(0, ...steps.map((st) => (compact ? K.stepCircle + K.stepGap : 56 + 12) + textBlock(st, px, compact ? K.stepLine : LINE_HEIGHT.normal, stepW)));
      // 한 줄이면 진행 막대 자리(22) · 여러 줄이면 줄 간격.
      return (header ? header + 52 : 0) + rows * ((compact ? 2 * K.stepPadY : 48) + col) + (rows > 1 ? (rows - 1) * (compact ? K.stepRowGap : 20) : 22);
    }
    case 'comparePair': {
      const w = Math.min(cw, 1600);
      const sideW = (w - 40) / 2 - 56;
      const side = (sd: unknown): number => {
        const o = (sd ?? {}) as Record<string, unknown>;
        return 48 + Math.max(T.header * LINE_HEIGHT.tight, textBlock(str(o.title), T.body, LINE_HEIGHT.tight, sideW - T.header - 16))
          + arr(o.body).map(str).reduce((a, l) => a + 16 + textBlock(l, T.caption, LINE_HEIGHT.loose, sideW), 0);
      };
      return (header ? header + 48 : 0) + Math.max(side(c.bad), side(c.good));
    }
    default:
      return null;
  }
}

/**
 * 🎯 이번 장면의 도형 목표 모양(px). 세이프존 core 안 · 가운데 정렬.
 *   ① 모양 표의 폭으로 안 내용 높이를 어림 ② core 높이를 넘으면 폭을 core 폭까지 넓혀 다시 어림 ③ 높이 = max(표 높이, 어림 + 여백) · core 높이 상한.
 *   posterStack 은 전체 화면(변신 끝점) — 글자는 레이아웃이 세이프존 안에 둔다.
 */
export function briefTargetRect(slide: BriefSlide, W: number, H: number, safe: BriefInsets, tailBottom?: number, tailWidth?: number): BriefRect {
  return briefFit(slide, W, H, safe, tailBottom, tailWidth).shape;
}

/** 최소 글자 배율 — 본문 56px × 0.72 ≈ 40px · 설명 44px × 0.72 ≈ 32px(가로 하한 29px 위). 이보다 줄여야 하면 넘침을 둔다. */
export const BRIEF_MIN_CONTENT_SCALE = 0.72;

/**
 * 🎯 도형 목표 모양 + 안 내용 배율. 글이 core 높이(자막 띠 위까지)에도 안 들어가면 안 내용을 도형 가운데 기준으로 줄인다
 *   (글자는 자르지 않는다 · 세이프존 밖으로 밀어내지도 않는다). 배율은 어림 높이로 정한다 — 넉넉한 어림이라 조금 더 줄어드는 쪽이다.
 */
export interface BriefFitResult {
  /** 촘촘한 간격으로 맞췄는가(체크리스트·단계·카드·한 문장) — 무대가 장표에 알려 같은 간격으로 그리게 한다. */
  compact?: boolean;
  /** 한 문장 장표 부제를 두 열로 맞췄는가(#6354) — 무대가 장표에 알려 같은 두 열로 그리게 한다. */
  subCols?: boolean;
  /** 안 내용 자리(여백 포함) — 장표 레이아웃은 이 안쪽(`padBottom`)에 놓인다. */
  rect: BriefRect;
  /** 화면에 그리는 도형 — 보통 `rect` 와 같고, 글이 자막 띠까지 닿으면 자막 띠를 품고 아래로 내려온다. */
  shape: BriefRect;
  /** 안 내용 아래 여백(px) — 자막 띠를 품으면 0(도형이 그 아래로 이어지므로). */
  padBottom: number;
  contentScale: number;
  /** 단계·카드 칸 계획(무대가 장표에 내려보낸다) — 없는 종류는 생략. */
  cells?: BriefCellPlan;
}

/**
 * @param tailBottom 자막 띠 아랫변 + 여백(px) — 주면 «글이 자막 띠 윗변까지 닿는» 장면에서 도형이 자막 띠를 품는다
 *   (도형은 글자가 아니라 세이프존 밖도 괜찮다 · 부모 관찰 ①② «위로 쏠림 · 도형이 작다»).
 */
export function briefFit(slide: BriefSlide, W: number, H: number, safe: BriefInsets, tailBottom?: number, tailWidth?: number, compact = false, subCols = false): BriefFitResult {
  const k = W / 1920;
  const shape = BRIEF_SHAPES[slide.type] ?? { w: 0.84, h: 0.56, r: 24 };
  if (slide.type === 'posterStack') {
    const full = { x: 0, y: 0, w: W, h: H, r: 0 };
    return { rect: full, shape: full, padBottom: 0, contentScale: 1 };
  }
  const coreW = W - safe.left - safe.right;
  const coreH = H - safe.top - safe.bottom;
  const padX = BRIEF_PAD.x * k, padY = BRIEF_PAD.y * k;
  const fit = (w: number): number => {
    const est = briefContentHeight(slide, (w - 2 * padX) / k, compact, subCols);
    return est == null ? 0 : est * k * 1.06 + 2 * padY; // 6% 여유 — 어림은 넉넉한 쪽으로
  };
  // 폭은 표의 값에서 시작해 글이 «표 높이 × 1.25» 안에 들 때까지 8%p 씩 넓힌다(core 폭 상한).
  //   좁은 도형에 긴 제목을 억지로 접으면 «20 / % 이하» 처럼 어색하게 끊긴다(09-30 운영 표본 실측).
  const nominalH = Math.min(coreH, H * shape.h * 1.25);
  let w = Math.min(coreW, W * shape.w);
  // 단계·카드: 라벨이 한 줄로 드는 줄 폭보다 좁게 잡지 않는다(칸이 내용에 맞춘다 · core 폭 상한).
  const cellsAtMax = briefCellPlan(slide, (coreW - 2 * padX) / k);
  if (cellsAtMax) w = Math.max(w, Math.min(coreW, (briefCellRowWidth(slide, cellsAtMax) ?? 0) * k + 2 * padX));
  let need = fit(w);
  //   ★넓혀서 실제로 낮아질 때만 넓힌다★ — 짧은 항목 5개(줄바꿈 없음)는 넓혀도 높이가 그대로라, 끝까지 넓히면
  //   «상자는 넓은데 글이 가운데에 작게 모인다»(09-30 부모 관찰 · 운영 표본 38.7초).
  while (need > nominalH && w < coreW) {
    const w2 = Math.min(coreW, w + W * 0.08);
    const need2 = fit(w2);
    if (need2 >= need - 1) break;
    w = w2; need = need2;
  }
  // 자막 띠 품기 — 글이 core 높이에 거의 닿으면(48px 안) 안 내용 자리를 core 전체로 두고 아래 여백을 없앤다(도형이 이어지므로).
  //   ★품을 때는 도형 폭 ≥ 자막 띠 폭 + 좌우 48px★ — 아니면 띠 양끝이 도형 밖으로 삐져나온다(09-30 부모 실측 «4억원» 장면) → 품지 않고 띠 위에서 멈춘다.
  const wideEnough = typeof tailWidth !== 'number' || w >= tailWidth + 2 * 48 * k;
  const swallow = typeof tailBottom === 'number' && tailBottom > H - safe.bottom && need > coreH - 48 * k && wideEnough;
  const h = swallow ? coreH : Math.min(coreH, Math.max(H * shape.h, need));
  const x = safe.left + (coreW - w) / 2;
  const y = safe.top + (coreH - h) / 2;
  const padBottom = swallow ? 0 : padY;
  const needInner = need - 2 * padY;
  const availInner = h - padY - padBottom;
  // 한 문장 장표를 촘촘히 다시 맞출 때만 하한을 낮춘다(#6354) — 그 밖은 종전 하한 그대로.
  const minScale = compact && slide.type === 'fullText' ? BRIEF_FULLTEXT_MIN_SCALE : BRIEF_MIN_CONTENT_SCALE;
  const contentScale = needInner > availInner ? Math.max(minScale, availInner / needInner) : 1;
  const r = Math.round(shape.r * k);
  const rect = { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h), r };
  const shapeRect = swallow ? { ...rect, h: Math.round((tailBottom as number) - rect.y) } : rect;
  const cells = briefCellPlan(slide, (rect.w - 2 * padX) / k);
  return { rect, shape: shapeRect, padBottom: Math.round(padBottom), contentScale, ...(cells ? { cells } : {}) };
}

/** 변신 스프링 — f 2.0Hz · ζ 0.78(0.5초 안에 자리 잡고 넘침 ≈2%). 도형(UI 소품)에만 쓴다(§0 GNWORKS 규율 ①). */
export const BRIEF_MORPH_SPRING = Object.freeze({
  stiffness: (2 * Math.PI * 2.0) ** 2,
  damping: 2 * 0.78 * 2 * Math.PI * 2.0,
  mass: 1,
});

/** 장면 시작 후 `tSec` 의 도형 — 앞 모양에서 이번 모양으로 폭·높이·모서리·중심을 각각 스프링으로. */
export function briefMorphRect(tSec: number, from: BriefRect, to: BriefRect): BriefRect {
  const p = springStep(Math.max(0, tSec), BRIEF_MORPH_SPRING.stiffness, BRIEF_MORPH_SPRING.damping, BRIEF_MORPH_SPRING.mass);
  const mix = (a: number, b: number) => a + (b - a) * p;
  const cx = mix(from.x + from.w / 2, to.x + to.w / 2);
  const cy = mix(from.y + from.h / 2, to.y + to.h / 2);
  const w = Math.max(0, mix(from.w, to.w));
  const h = Math.max(0, mix(from.h, to.h));
  return { x: cx - w / 2, y: cy - h / 2, w, h, r: Math.max(0, mix(from.r, to.r)) };
}

/** 변신 스프링이 진행도 `p` 에 처음 닿는 시각(초) — 이분법(순수 · 모듈 적재 때 한 번). */
export function briefMorphTimeAt(p: number): number {
  let lo = 0, hi = 2;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (springStep(mid, BRIEF_MORPH_SPRING.stiffness, BRIEF_MORPH_SPRING.damping, BRIEF_MORPH_SPRING.mass) >= p) hi = mid; else lo = mid;
  }
  return hi;
}

/** 안 내용 창(초) — 도형이 80% 자란 순간(≈0.25초)부터 0.12초 동안 들어오고, 다음 변신 0.1초 전에 빠진다.
 *  ★시간이 아니라 변신 진행도에 묶는다★ — §12-5 초안의 0.08초는 원쉐이프(더 빠른 변신) 값이라, 이 스프링에서는 도형이 아직 작을 때
 *  글자가 도형 밖에 비쳤다(09-30 운영 표본 실측 · 영상 씬 뒤 씨앗에서 자랄 때 4상자). */
export const BRIEF_CONTENT_LEAD_SEC = briefMorphTimeAt(0.8);

/**
 * 이번 변신에서 글자가 들어오기 시작할 시각(초) — 도형 가장자리가 목표 자리와 `tolPx` 안으로 들어온 순간.
 *   변신 거리에 맞춘다: 가까운 변신은 0.8 진행도(≈0.19초), 씨앗에서 크게 자라는 변신은 더 늦게(≈0.3초).
 *   «0.8 진행도» 고정은 씨앗(140px) → 1613px 변신에서 글자 가장자리가 70px 씩 도형 밖에 비쳤다(09-30 운영 표본 256프레임).
 */
export function briefContentLead(from: BriefRect, to: BriefRect, tolPx: number): number {
  const edge = Math.max(
    Math.abs(from.x - to.x), Math.abs(from.y - to.y),
    Math.abs(from.x + from.w - (to.x + to.w)), Math.abs(from.y + from.h - (to.y + to.h)),
  );
  if (edge <= tolPx) return BRIEF_CONTENT_LEAD_SEC;
  const need = Math.min(0.99, Math.max(0.8, 1 - tolPx / edge));
  return briefMorphTimeAt(need);
}
export const BRIEF_CONTENT_FADE_SEC = 0.12;
export const BRIEF_CONTENT_TAIL_SEC = 0.1;
